import React, { memo, useMemo, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import type { richText_nativeIssueTable_RichTextCell_fieldRef$key } from '@atlassian/jira-relay/src/__generated__/richText_nativeIssueTable_RichTextCell_fieldRef.graphql';
import type { richText_nativeIssueTable_RichTextCell_issueRef$key } from '@atlassian/jira-relay/src/__generated__/richText_nativeIssueTable_RichTextCell_issueRef.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import {
	useIsDensityFull,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { PACKAGE_NAME } from '../../../constants.tsx';
import { RichTextCellCompact } from './rich-text-compact/index.tsx';
import { RichTextCellFull } from './rich-text-full/index.tsx';
import { richTextPopUpContentEntryPoint } from './rich-text-popup-content/entrypoint.tsx';
import { ErrorState } from './error-state/index.tsx';

export type Props = {
	issueKey: string;
	fieldRef: richText_nativeIssueTable_RichTextCell_fieldRef$key;
	/**
	 * Should only be needed For VR testing, to render a storybook example with
	 * the popup content immediately visible
	 */
	shouldOpenOnFirstRender?: boolean;
	issueRef?: richText_nativeIssueTable_RichTextCell_issueRef$key | null;
};

export const RichTextCell = memo<Props>(
	({ issueKey, fieldRef, issueRef, shouldOpenOnFirstRender = false }: Props) => {
		const { richText, fieldId } = useFragment<richText_nativeIssueTable_RichTextCell_fieldRef$key>(
			graphql`
				fragment richText_nativeIssueTable_RichTextCell_fieldRef on JiraRichTextField
				@argumentDefinitions(isDensityFull: { type: "Boolean!" }) {
					fieldId
					richText {
						adfValue {
							convertedPlainText @skip(if: $isDensityFull) {
								plainText
							}
							json @include(if: $isDensityFull)
						}
					}
				}
			`,
			fieldRef,
		);

		const issueData = useFragment<richText_nativeIssueTable_RichTextCell_issueRef$key>(
			graphql`
				fragment richText_nativeIssueTable_RichTextCell_issueRef on JiraIssue
				@argumentDefinitions(isDensityFull: { type: "Boolean!" }) {
					canBeExported @include(if: $isDensityFull)
				}
			`,
			issueRef || null,
		);

		const isDensityFull = useIsDensityFull();

		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
		const cloudId = useCloudId();
		const entryPointParams = useMemo(
			() => ({ fieldId, issueKey, cloudId }),
			[fieldId, cloudId, issueKey],
		);
		const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
			richTextPopUpContentEntryPoint,
			entryPointParams,
		);
		const descriptionContentRef = useRef(null);

		if (isSingleLineRowHeightEnabled) {
			return (
				<HoverPopover
					contentRef={descriptionContentRef}
					content={
						<JiraEntryPointContainer
							entryPointReferenceSubject={entryPointReferenceSubject}
							id="rich-text-popup-content"
							packageName={PACKAGE_NAME}
							errorFallback={() => <ErrorState />}
							fallback={<Spinner size="medium" />}
							runtimeProps={{}}
						/>
					}
					entryPointActions={entryPointActions}
				>
					<Box xcss={richTextCellWrapperStyles} ref={descriptionContentRef}>
						{richText?.adfValue?.convertedPlainText?.plainText}
					</Box>
				</HoverPopover>
			);
		}

		if (isDensityFull) {
			return (
				<RichTextCellFull
					issueKey={issueKey}
					richText={richText?.adfValue?.json || ''}
					canBeExported={issueData?.canBeExported}
				/>
			);
		}

		return (
			<RichTextCellCompact
				fieldId={fieldId}
				plainText={richText?.adfValue?.convertedPlainText?.plainText || ''}
				issueKey={issueKey}
				shouldOpenOnFirstRender={shouldOpenOnFirstRender}
			/>
		);
	},
);

const richTextCellWrapperStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});
